import React from 'react';
import { classNames } from '@/lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  children,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const variantClasses = {
    primary: 'border border-aura-primary bg-aura-primary text-on-primary shadow-botanical hover:border-aura-primary-dim hover:bg-aura-primary-dim',
    secondary: 'border border-aura-secondary/30 bg-aura-secondary-container text-on-secondary-container hover:border-aura-secondary/50 hover:bg-aura-secondary-container/80',
    danger: 'border border-aura-error bg-aura-error text-on-error hover:border-aura-error/90 hover:bg-aura-error/90',
    ghost: 'border border-aura-text/20 bg-white text-aura-text/70 shadow-sm hover:border-aura-primary/30 hover:bg-aura-background hover:text-aura-text',
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-[13px] min-h-[36px]',
    md: 'px-6 py-3 text-[14px] min-h-[44px]',
    lg: 'px-8 py-4 text-[16px] min-h-[56px]',
  };

  return (
    <button
      {...props}
      disabled={disabled}
      className={classNames(
        'inline-flex items-center justify-center gap-2 font-bold rounded-full transition-[background-color,border-color,color,box-shadow,transform] duration-200 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/40 focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none',
        fullWidth ? 'w-full' : 'w-auto',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {children}
    </button>
  );
}

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'elevated';
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}

export function Card({ children, className, variant = 'default', onClick, style }: CardProps) {
  return (
    <div
      onClick={onClick}
      style={style}
      className={classNames(
        'bg-white p-5 transition-all duration-200',
        variant === 'elevated' ? 'rounded-[2rem] shadow-botanical-lg' : 'rounded-[1rem] shadow-botanical',
        onClick ? 'cursor-pointer active:scale-[0.98]' : '',
        className
      )}
    >
      {children}
    </div>
  );
}

interface BannerProps {
  type: 'info' | 'success' | 'warning' | 'error';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function Banner({ type, children, className, style }: BannerProps) {
  const typeClasses = {
    info: 'bg-aura-secondary-container text-on-secondary-container border-aura-secondary/20',
    success: 'bg-aura-accent/20 text-aura-primary border-aura-primary/10',
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    error: 'bg-aura-error-container text-on-error-container border-aura-error/10',
  };

  return (
    <div style={style} className={classNames(
      'px-4 py-3 rounded-xl border mb-4 text-sm font-medium',
      typeClasses[type],
      className
    )}>
      {children}
    </div>
  );
}

interface LoadingSpinnerProps {
  message?: string;
  className?: string;
}

export function LoadingSpinner({ message, className }: LoadingSpinnerProps) {
  return (
    <div className={classNames('flex flex-col items-center justify-center p-10 gap-4', className)}>
      <div className="w-10 h-10 border-3 border-aura-text/10 border-t-aura-primary rounded-full animate-spin" />
      {message && <span className="text-aura-text/40 text-sm font-medium">{message}</span>}
    </div>
  );
}

interface EmptyStateProps {
  icon?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={classNames('flex flex-col items-center justify-center p-12 text-center', className)}>
      {icon && <div className="text-5xl mb-4">{icon}</div>}
      <div className="text-lg font-black text-aura-text mb-2 uppercase tracking-widest">{title}</div>
      {description && <div className="text-sm text-aura-text/40 max-w-[280px] font-medium leading-relaxed">{description}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  maxWidth?: string;
}

export function Modal({ isOpen, onClose, title, children, size = 'md', maxWidth }: ModalProps) {
  const titleId = React.useId();

  if (!isOpen) return null;

  const sizeClasses = {
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  const widthClass = maxWidth || sizeClasses[size];

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="fixed inset-0" 
        onClick={onClose} 
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={classNames("relative w-full bg-white rounded-[2rem] shadow-botanical-lg overflow-hidden animate-in slide-in-from-bottom-4 duration-300", widthClass)}
      >
        <div className="flex items-center justify-between p-6 border-b border-aura-primary/5">
          <h3 id={titleId} className="text-lg font-black text-aura-text uppercase tracking-widest">{title}</h3>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center text-aura-text/40 hover:bg-aura-background transition-colors"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export function Input({ label, id, className, ...props }: InputProps) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  return (
    <div className="space-y-1.5 w-full">
      {label && <label htmlFor={inputId} className="text-[10px] font-black text-aura-text/40 uppercase tracking-[2px] ml-4">{label}</label>}
      <input
        id={inputId}
        {...props}
        className={classNames(
          "w-full bg-aura-background border-none rounded-2xl px-5 py-4 text-aura-text font-bold text-sm placeholder:text-aura-text/20 focus:ring-2 focus:ring-aura-primary/20 outline-none transition-all",
          className
        )}
      />
    </div>
  );
}
